import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import styled, { css } from 'styled-components/macro';
import { withRouter } from 'react-router';
import { authActions } from 'src/store/auth/auth.actions';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as IconMenu } from 'src/assets/svg/icon-menu.svg';
import { ReactComponent as IconMenuClose } from 'src/assets/svg/icon-menu-close.svg';
import { motion } from 'framer-motion';
import {
  faEnvelope, faIdCard, faKey, faUserCircle,
} from '@fortawesome/free-solid-svg-icons';
import { profileActions } from 'src/store/profile/profile.actions';
import { getHelptexts, getIsSuperuser, getPrognosisRegistrationIsAvailible } from 'src/store/profile/profile.selectors';
import {
  getBrandingImage,
  imageTypes,
  colorTypes,
  getBrandingColor,
  getBrandingStyleOverride,
  styleOverrides,
} from 'src/branding';
import Cookies from 'js-cookie';
import qs from 'qs';
import { useToast } from './components/Toast';
import { format } from './utils/date.utils';
import { backendUrl, company } from './config';
import Modal from './components/Modal';
import { getProfile } from './store/profile/profile.selectors';
import { routes } from './routes/routes.manifest';
import {
  Row, LinkLabel, Icon, Label, Column, HTML,
} from './components/Layout';
import { desktopBreakpointSize } from './routes/Dashboard/Dashboard';
import { getFeaturesEnabled } from './slaughterhouseSpecific';
import axiosInstance from './axios-instance';
import { initiateBrandingClothesRedirect } from './utils/brandingclothes.utils';

const MenuRow = styled(Row)`
  width: 100%;
  padding: 1em 0;
  height: 5rem;
  ${getBrandingStyleOverride('menuRow')}

  ${({ inDropdown }) => inDropdown
    && css`
      padding-bottom: 0;
    `}
`;

const MenuWrapper = styled(MenuRow)`
  ${({ loggedIn }) => (loggedIn
  ? getBrandingStyleOverride('topMenuLoggedIn')
  : getBrandingStyleOverride('topMenuNotLoggedIn'))}

  ${getBrandingStyleOverride('topMenu')}

    ${LogoImg}.for-desktop{
      display: block;
    }

    ${LogoImg}.for-mobile{
      display: none;
    }

  @media screen and (max-width: ${desktopBreakpointSize}){
    ${getBrandingStyleOverride(styleOverrides.topMenuTabletMobileSize)};

    ${LogoImg}.for-mobile{
      display: block;
    }

    ${LogoImg}.for-desktop{
      display: none;
    }
  }
`;

const LogoImg = styled.img`
  flex: 0 0 4em;
  display: block;
  align-self: flex-start;
  margin-left: 1em;
  margin-right: auto;
  padding-bottom: 7px;
  z-index: 0;
  width: 63px;
  height: auto;
  padding-bottom: 0;
  height: 100%;
  flex-basis: 100%;
  width: auto;
  user-select: none;

  :hover {
    cursor: pointer;
  }

  ${getBrandingStyleOverride(styleOverrides.menuLogo)};

  ${company === 'midtnorge'
  ? css`
        /*IE10-11*/
        @media screen and (-ms-high-contrast: active),
          (-ms-high-contrast: none) {
          width: 250px;
        }

        /*Edge 12-18*/
        @supports (-ms-ime-align: auto) {
          width: 250px;
        }
      `
  : ''}

  @media (max-width: ${desktopBreakpointSize}){
    ${getBrandingStyleOverride(styleOverrides.menuLogoMobile)};
  }
`;

const MenuIcon = styled.svg`
  rect {
    fill: ${({ color }) => color};
  }
  flex: 0 0 auto;
  max-width: 2em;
  margin-left: auto;
  margin-right: 1em;
  height: 2em;
  padding: 0;
`;

const menuVariants = {
  open: {
    height: 'auto',
    transition: {
      duration: 0.25,
      // ease: easing.easeOut,
    },
  },
  closed: {
    height: '0px',
    transition: {
      duration: 0.25,
      // ease: easing.easeOut,
    },
  },
};

const RouteLabel = styled(LinkLabel)`
  color: white;
  text-align: center;
  display: block;
  padding: 0;
  margin: 0;
  min-width: min-content;
  justify-content: center;
  font-size: 1.6em;
  line-height: 2;
  font-weight: normal;
  cursor: pointer;
  :hover,
  :focus,
  :active {
    text-decoration: underline;
  }
`;

const LogoutLabel = styled(RouteLabel)`
  border-top: 1px solid #fff;
  border-bottom: 1px solid #fff;
  margin-top: 1em;
`;

const getMenuRoutes = (enableWorkwearPunchout) => [
  {
    text: 'Innmelding',
    to: routes.registerEnrolment,
  },
  {
    text: 'Avregninger',
    to: routes.accountingsPosted,
  },
  {
    text: 'Dokumenter',
    to: routes.documents,
  },
  {
    text: 'Statistikk',
    to: routes.statistics,
  },
  {
    text: 'Mine storfe',
    to: routes.myCattle,
  },
  {
    text: 'Profileringsklær',
    id: 'profileClothes',
    to: enableWorkwearPunchout ? '#' : routes.brandingClothes,
    onClick: enableWorkwearPunchout ? initiateBrandingClothesRedirect : undefined,
  },
  { text: 'Prognose slakt og kjøp/salg livdyr', id: 'prognosisRegistration', action: 'openPrognoisRegistration' },
  {
    text: 'Retur',
    to: routes.returnFormHelp,
  },
];

const MenuContent = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  background-color: ${getBrandingColor(colorTypes.primary)};
  overflow: hidden;
  z-index: 10000;
`;

const MessagesButton = styled(LinkLabel)`
  display: flex;
  position: relative;
  align-items: center;
  padding: 0 1.5em;

  ${Icon} {
    font-size: 2em;
    margin: auto;
  }
`;

const OpenCloseButton = styled(LinkLabel)`
  display: flex;
  align-items: center;
  padding: 0px 1.5em;

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    /* IE10+ specific styles go here */
    width: 1.9em;
  }

`;

const ImpersonateButton = styled(MessagesButton)``;
const SetNewPasswordButton = styled(MessagesButton)``;
const MyProfileButton = styled(MessagesButton)``;

const MessagesBadgeNumber = styled.div`
  position: absolute;
  bottom: 50%;
  left: 50%;
  width: 1.6em;
  line-height: 1.6;
  font-size: 0.9em;
  background-color: #c20000;
  text-align: center;
  color: #fff;
  border-radius: 50%;
`;

const Menu = withRouter(({ profile, history, hideControls = false }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [messagesModalOpen, setMessagesModalOpen] = useState(false);
  const [messages, setMessages] = useState(null);
  const [lastReadMessages, setLastReadMessages] = useState({});
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(null);
  const userHasReadMessage = (id, date) => lastReadMessages[id] && lastReadMessages[id];
  const prognosisRegistationIsAvailible = useSelector(getPrognosisRegistrationIsAvailible);

  const interval = useRef(null);

  const actions = {
    openPrognoisRegistration: () => dispatch(profileActions.openPrognosisRegistration()),
  };

  let checkForMessages = null;
  checkForMessages = useCallback(() => {
    axiosInstance
      .request({
        method: 'GET',
        url: `${backendUrl}/api/external/messages`,
        withCredentials: true,
      })
      .then(({ data }) => {
        if (data.messages) {
          setMessages(data.messages);
        }
      })
      .catch();
    axiosInstance
      .request({
        method: 'GET',
        url: `${backendUrl}/api/hasread/messages`,
        withCredentials: true,
      })
      .then(({ data }) => {
        if (data.items) {
          setLastReadMessages(data.items);
        }
      });

    if (interval.current) {
      clearTimeout(interval.current);
    }
    interval.current = setTimeout(checkForMessages, 120000); // 2 minutes
  }, [checkForMessages]);

  const markRead = useCallback(
    (messageId) => axiosInstance
      .request({
        method: 'POST',
        url: `${backendUrl}/api/markread/messages/${messageId}`,
        withCredentials: true,
      })
      .then(({ data: { id, timestamp } }) => {
        setLastReadMessages((messages) => ({
          ...messages,
          [id]: {
            id,
            timestamp,
          },
        }));
      }),
    [],
  );

  useEffect(() => {
    checkForMessages();

    return () => {
      if (interval.current) {
        clearTimeout(interval.current);
      }
    };
  }, [checkForMessages]);

  const helptexts = useSelector(getHelptexts);

  useEffect(() => {
    if (!helptexts.isFetching && !helptexts.error) {
      const now = new Date();
      if (
        helptexts.lastFetched === null
        || now.getTime() - helptexts.lastFetched.getTime() > 600000
      ) {
        dispatch(profileActions.fetchHelptexts());
      }
    }
  }, [
    dispatch,
    helptexts.isFetching,
    helptexts.lastFetched,
    open,
    helptexts.error,
  ]);

  useEffect(() => {
    if (selectedMessage !== null && messages[selectedMessage]) {
      const { id } = messages[selectedMessage];
      markRead(id);
    }
  }, [selectedMessage, messages, markRead]);

  useEffect(() => {
    if (messages && lastReadMessages) {
      if (
        !lastReadMessages.notification
        || !lastReadMessages.notification.timestamp
      ) {
        setUnreadMessagesCount(messages.length);
        return;
      }

      setUnreadMessagesCount(
        messages.filter((m) => !lastReadMessages[m.id]).length,
      );
    }
  }, [messages, lastReadMessages]);

  useEffect(() => {
    if (messagesModalOpen) {
      checkForMessages();
      markRead('notification');
    }
  }, [checkForMessages, markRead, messagesModalOpen]);

  const logOut = () => {
    setOpen(false);
    dispatch(authActions.logout());
  };

  const goTo = (to) => {
    setOpen(false);
    history.push(to);
  };

  const profileSelector = useSelector(getProfile);
  const toast = useToast();

  const enterImpersonateUserMode = () => {
    const user = prompt(
      "**Overta brukernavn**\n\n Alle forespørsler blir gjort som brukeren, med unntak av:\n- Eposter blir sendt til superbruker-eposten\n- Ting blir ikke markert som lest/sett.\nSjekk profil-siden for å se om overtakelsen var suksessfull\n \nSkriv 'avslutt' for å gå ut av impersonate-modusen.\n\n Brukernavn:",
    );
    if (user) {
      if (user.toLowerCase() === 'avslutt') {
        localStorage.removeItem('impersonateUsername');
        toast.addMessage('Tilbake til vanlig modus');
      } else {
        localStorage.setItem('impersonateUsername', user);
        toast.addMessage(`Du blir nå ${user}!(hvis brukeren finnes)`);
      }

      setTimeout(() => {
        window.location.reload(true);
      }, 2000);
    }
  };

  const showPasswordResetDialog = () => {
    const username = prompt('Send reset-passord lenke til superbruker-eposten');
    if (username) {
      const req = axiosInstance
        .request({
          method: 'POST',
          url: `${backendUrl}/api/forgot_password_superuser`,
          data: qs.stringify({ username }),
          withCredentials: true,
        })
        .then((resp) => {
          if (resp.status === 200) {
            toast.addMessage(
              'Sendte passord-reset lenke (hvis brukeren eksisterer)',
            );
          } else {
            toast.addError('Kunne ikke sende passord-reset lenke');
          }
        })
        .catch((e) => {
          toast.addError('Kunne ikke sende passord-reset lenke');
        });
    }
  };

  const isSuperuser = useSelector(getIsSuperuser);
  const menuRoutes = React.useMemo(() => getMenuRoutes(isSuperuser), [isSuperuser]);

  return (
    <>
      <MenuWrapper loggedIn={Cookies.get('logged-in')}>
        <LogoImg
          name="logo"
          className="for-mobile"
          hideControls={hideControls}
          aria-label="Gå til dashboard"
          role="button"
          src={getBrandingImage(imageTypes.menuLogoMenuClosedMobile)}
          onClick={() => goTo(routes.dashboard)}
        />

        <LogoImg
          className="for-desktop"
          name="logo"
          hideControls={hideControls}
          aria-label="Gå til dashboard"
          role="button"
          src={getBrandingImage(imageTypes.menuLogoMenuClosed)}
          onClick={() => goTo(routes.dashboard)}
        />

        {localStorage.getItem('impersonateUsername') && (
          <p style={{ alignSelf: 'center' }}>
            Inmpersonated:
            {' '}
            {localStorage.getItem('impersonateUsername')}
          </p>
        )}
        {((!hideControls
          && profileSelector
          && profileSelector.is_superuser
          && profileSelector.is_superuser.is_superuser)
          || localStorage.getItem('impersonateUsername')) && (
            <ImpersonateButton
              name="superuser-impersonate"
              title="Ta over brukerkonto"
              onClick={enterImpersonateUserMode}
            >
              <Icon
                color={getBrandingColor(colorTypes.primary)}
                icon={faIdCard}
              />
            </ImpersonateButton>
        )}

        {!hideControls
          && profileSelector
          && profileSelector.is_superuser
          && profileSelector.is_superuser.is_superuser && (
            <SetNewPasswordButton
              name="superuser-resetpassword"
              title="Sett nytt passord for bruker"
              onClick={showPasswordResetDialog}
            >
              <Icon
                color={getBrandingColor(colorTypes.primary)}
                icon={faKey}
              />
            </SetNewPasswordButton>
        )}

        {company === 'midtnorge' && (
          <MyProfileButton
            name="profile"
            onClick={() => {
              history.push(routes.profile);
            }}
            aria-label="Min profil"
          >
            <Icon
              color={getBrandingColor(colorTypes.primary)}
              icon={faUserCircle}
            />
          </MyProfileButton>
        )}

        {!hideControls && messages && !!messages.length && (
          <MessagesButton
            name="messages"
            marginLeft="auto"
            onClick={() => {
              setMessagesModalOpen(true);
              setSelectedMessage(null);
            }}
          >
            <Icon
              color={getBrandingColor(colorTypes.primary)}
              icon={faEnvelope}
            />
            {!!unreadMessagesCount && (
              <MessagesBadgeNumber>
                {unreadMessagesCount > 9 ? '9+' : unreadMessagesCount}
              </MessagesBadgeNumber>
            )}
          </MessagesButton>
        )}
        {!hideControls && (
          <OpenCloseButton
            name="openclose"
            marginLeft="auto"
            onClick={() => setOpen((open) => !open)}
          >
            <MenuIcon
              color={getBrandingColor(colorTypes.primary)}
              as={IconMenu}
            />
          </OpenCloseButton>
        )}
        <MenuContent
          variants={menuVariants}
          animate={open ? 'open' : 'closed'}
          initial="closed"
        >
          <MenuRow inDropdown>
            <LogoImg
              name="logo"
              aria-hidden="true"
              role="button"
              className="for-mobile is-open"
              src={getBrandingImage(imageTypes.menuLogoMenuOpenMobile)}
              onClick={() => goTo(routes.dashboard)}
            />
            <LogoImg
              name="logo"
              aria-hidden="true"
              role="button"
              className="for-desktop is-open"
              src={getBrandingImage(imageTypes.menuLogoMenuOpen)}
              onClick={() => goTo(routes.dashboard)}
            />
            <LinkLabel
              marginLeft="auto"
              onClick={() => setOpen((open) => !open)}
            >
              <MenuIcon
                color="#fff"
                as={IconMenuClose}
                style={{ padding: '1.5em' }}
              />
            </LinkLabel>
          </MenuRow>
          {menuRoutes
            .filter(
              (r) => r.text !== 'Mine storfe'
                || (r.text === 'Mine storfe'
                  && profileSelector.has_cattle.has_cattle === true),
            )
            .filter((r) => (r.id === 'profileClothes' && getFeaturesEnabled().branding_clothes) || r.text !== 'profileClothes')
            .filter((r) => (r.id === 'prognosisRegistration' && getFeaturesEnabled().prognosisRegistration && prognosisRegistationIsAvailible) || r.id !== 'prognosisRegistration')
            .map(({
              text, to, action, onClick,
            }, key) => (
              <RouteLabel
                key={key}
                size="1.6em"
                lineHeight="2"
                color="#fff"
                textDecoration="none"
                justify="center"
                onClick={(e) => {
                  if (onClick) {
                    onClick(e, dispatch, toast);
                    setOpen(false);

                    return;
                  }
                  if (action) {
                    actions[action]();
                  }
                  goTo(to);
                }}
              >
                {text}
              </RouteLabel>
            ))}
          <LogoutLabel
            size="1.4em"
            lineHeight="2.7"
            color="#fff"
            textDecoration="none"
            justify="center"
            onClick={logOut}
          >
            Logg ut
          </LogoutLabel>
        </MenuContent>
      </MenuWrapper>
      {messagesModalOpen && (
        <Modal
          onClose={() => setMessagesModalOpen(false)}
          closable
          width="900px"
        >
          {selectedMessage !== null && messages[selectedMessage] ? (
            (({ title, body, date }) => (
              <Column shrink overflowY="auto">
                <LinkLabel
                  size="1.1em"
                  textDecoration="none"
                  onClick={() => setSelectedMessage(null)}
                >
                  &larr;&nbsp; Vis alle meldinger
                </LinkLabel>
                <Label
                  font="serif"
                  as="h1"
                  size="2.5em"
                  color="#68696d"
                  marginTop="0.5em"
                >
                  {title}
                </Label>
                <Label
                  font="serif"
                  size="1.1em"
                  color="#68696d"
                  marginBottom="0.75em"
                >
                  {format(new Date(date), 'dd. MMMM yyyy')}
                </Label>
                <Label>
                  <HTML>{body}</HTML>
                </Label>
              </Column>
            ))(messages[selectedMessage])
          ) : (
            <Column shrink>
              <Label
                font="serif"
                as="h1"
                size="2.5em"
                color="#68696d"
                marginBottom="0.5em"
                style={{ textTransform: 'initial' }}
              >
                Meldinger
              </Label>
              <Column shrink overflowY="auto">
                {messages.map(({ title, id, date }, key) => (
                  <LinkLabel key={key} onClick={() => setSelectedMessage(key)}>
                    <Row padding="0.5em 0" align="center">
                      <Label
                        css={
                          !userHasReadMessage(id, date)
                            ? css`
                                font-weight: bold;
                              `
                            : css`
                                font-weight: normal;
                              `
                        }
                        size="1.1em"
                      >
                        {title}
                      </Label>
                      <Label font="serif" color="#68696d" marginLeft="auto">
                        {format(new Date(date), 'dd. MMMM yyyy')}
                      </Label>
                    </Row>
                  </LinkLabel>
                ))}
              </Column>
            </Column>
          )}
          {' '}
        </Modal>
      )}
    </>
  );
});

export default Menu;
